import { useI18n } from "@/composables/useI18n";
import { useOpenInNewTab } from "@/composables/useOpenInNewTab";
import { db } from "@/firebase";
import { useToast } from "@/store";
import type { Account } from "@aircap/common/account";
import { type ContractEnvelopeRecipientKey } from "@aircap/common/signatureApi";
import { getContractCustomFilePath } from "@aircap/common/storage";
import type { SupervisorOrg } from "@aircap/common/supervisorOrg";
import { captureException } from "@sentry/vue";
import { doc, updateDoc } from "firebase/firestore";
import { deleteObject, ref as storageRef } from "firebase/storage";
import { computed, ref, type Ref } from "vue";
import { useFirebaseStorage, useStorageFile } from "vuefire";

export function areSupervisorOrgContractInfoValid(
  supervisorOrg: SupervisorOrg | null | undefined,
  templates: { contractSignatureContact?: { email: string; name: string } }[],
): boolean {
  const hasTemplate = templates.length > 0;
  const hasDefaultContact =
    !!supervisorOrg?.contractSignatureContact?.email &&
    !!supervisorOrg?.contractSignatureContact?.name;
  const hasTemplateWithContact = templates.every(
    (template) =>
      !!template.contractSignatureContact?.email && !!template.contractSignatureContact?.name,
  );
  return hasTemplate && (hasDefaultContact || hasTemplateWithContact);
}

export const useContract = (accountId: string, account: Ref<Account | null | undefined>) => {
  const { t } = useI18n();
  const { showSuccessToast, showErrorToast } = useToast();
  const storage = useFirebaseStorage();
  const openInNewTab = useOpenInNewTab();

  const contractType = ref<"signatureApi" | "custom" | null>(null);
  const contractCustomFileInput = ref<File | null>(null);
  const deletingContract = ref(false);

  const contractCustomRef = computed(() =>
    storageRef(storage, getContractCustomFilePath({ accountId })),
  );
  const { upload: uploadContractCustom } = useStorageFile(contractCustomRef);

  const signatureAPIStatuses = computed(() => {
    const recipients = account.value?.contractEnvelope?.recipients;
    if (!recipients) {
      return {
        operator: "error : missing recipients",
        supervisorOrg: "error : missing recipients",
      };
    }
    return {
      operator:
        recipients.find(
          ({ key }) => key === ("account_holder" satisfies ContractEnvelopeRecipientKey),
        )?.status || "pending",
      supervisorOrg:
        recipients.find(({ key }) => key === ("client" satisfies ContractEnvelopeRecipientKey))
          ?.status || "pending",
    };
  });

  const maxContractFileSizeMB = 32;

  const downloadContract = () => {
    openInNewTab("contract", { accountId });
  };

  const handleDeleteContractCustom = async () => {
    deletingContract.value = true;
    try {
      console.log("Deleting contract...");
      await deleteObject(contractCustomRef.value);

      const accountRef = doc<Account>(db, `accounts/${accountId}`);
      await updateDoc(accountRef, { contractType: null });

      showSuccessToast(t("account.settings.contract.deleteSuccess"));
    } catch (cause) {
      const error = new Error("Error deleting custom contract", { cause });
      console.error(error);
      captureException(error);
      showErrorToast(t("account.settings.contract.deleteError"));
    } finally {
      deletingContract.value = false;
    }
  };

  const validateContractCustomFileInput = (): boolean => {
    if (
      contractCustomFileInput.value &&
      contractCustomFileInput.value.size > maxContractFileSizeMB * 1024 * 1024
    ) {
      showErrorToast(t("errors.fileTooBig", { maxFileSizeMB: maxContractFileSizeMB }));
      return false;
    }
    return true;
  };

  const initializeFromAccount = (accountData: Account | null | undefined) => {
    if (accountData) {
      contractType.value = accountData.contractType ?? null;
    }
  };

  return {
    contractType,
    contractCustomFileInput,
    deletingContract,
    signatureAPIStatuses,
    maxContractFileSizeMB,
    downloadContract,
    handleDeleteContractCustom,
    uploadContractCustom,
    validateContractCustomFileInput,
    initializeFromAccount,
  };
};
